import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { PRACTICE_SETTINGS_CONTROLLER } from "api/Service";
import { CANCELLATION_REASONS_QUERY_KEY } from "features/practice/hooks/useCancellationReasons";
import { CONSULT_OUTCOMES_QUERY_KEY } from "features/practice/hooks/useConsultOutcomes";
import { CONSULT_TIMES_QUERY_KEY } from "features/practice/hooks/useConsultTimes";
import { CONSULT_TYPES_QUERY_KEY } from "features/practice/hooks/useConsultTypes";
import { REASON_FOR_ABSENCE_QUERY_KEY } from "features/practice/hooks/useReasonForAbsence";
import { USER_ROLES_QUERY_KEY } from "features/practice/hooks/useUserRoles";
import { WHAT_CANT_BE_ATTENDED_QUERY_KEY } from "features/practice/hooks/useWhatCantBeAttended";
import { useHandleNetworkError } from "utils/useHandleNetworkError";

// notifyOnSuccess: the Practice Settings screen saves automatically on every
// toggle, so it opts out of the per-save toast and shows an inline status instead.
export const useUpdatePracticeSettings = ({ notifyOnSuccess = true } = {}) => {
  const queryClient = useQueryClient();
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const handleNetworkError = useHandleNetworkError("Error saving practice settings");

  return useMutation({
    /*
     * PATCH, not POST (KIN-1994). The POST replaced the whole settings row, so any field the
     * body left out was reset to its model default — that is how saving one toggle switched
     * laboratory ordering off for a practice (KIN-1885). PATCH changes only what the body
     * names.
     *
     * `data` is therefore the changed settings and nothing else. `tenant` and `id` are not
     * settings and must not be sent: the backend answers an unrecognised field name with a
     * 400 naming it, and the tenant comes from the path and the token.
     */
    mutationFn: async (data) => {
      const axiosInstance = createAxiosInstance();

      await axiosInstance.patch(
        `${tenantVal}${PRACTICE_SETTINGS_CONTROLLER}`,
        data,
        { headers: { "Content-Type": "application/json" } }
      );
    },
    onSuccess: () => {
      if (notifyOnSuccess) {
        toast.success("Practice settings saved successfully.");
      }

      queryClient.invalidateQueries({ queryKey: ["practiceSettings", tenantVal] });
      queryClient.invalidateQueries({
        queryKey: [CONSULT_TYPES_QUERY_KEY, tenantVal],
      });
      queryClient.invalidateQueries({
        queryKey: [CONSULT_TIMES_QUERY_KEY, tenantVal],
      });
      queryClient.invalidateQueries({
        queryKey: [CANCELLATION_REASONS_QUERY_KEY, tenantVal],
      });
      queryClient.invalidateQueries({
        queryKey: [CONSULT_OUTCOMES_QUERY_KEY, tenantVal],
      });
      queryClient.invalidateQueries({
        queryKey: [REASON_FOR_ABSENCE_QUERY_KEY, tenantVal],
      });
      queryClient.invalidateQueries({
        queryKey: [WHAT_CANT_BE_ATTENDED_QUERY_KEY, tenantVal],
      });
      queryClient.invalidateQueries({
        queryKey: [USER_ROLES_QUERY_KEY, tenantVal],
      });
    },
    onError: handleNetworkError,
  });
};
